import {
  DESIGN_WIDTH,
  DESIGN_HEIGHT,
  DIMENSION_CARD_WIDTH,
  DIMENSION_CARD_IMAGE_TOP,
  DIMENSION_CARD_IMAGE_HEIGHT,
} from '../data/homeDimensions'

/** Board reference timing — shared across GSAP ScrollTrigger sections */
export const BOARD_SCRUB = 0.1
export const BOARD_DESKTOP_MQ = '(min-width: 1260px)'
export const BOARD_CHAR_OPACITY_MIN = 0.3
export const BOARD_CHAR_OPACITY_MAX = 1
export const BOARD_CHAR_STAGGER = 0.3
export const BOARD_SLIDE_SWAP_THRESHOLD = 0.5
export const BOARD_SLIDE_OFFSET_PX = 48
/** Pin scroll distance multipliers (× viewport height). GSAP pinSpacing owns scroll length — do not duplicate in CSS height. */
export const BOARD_MISSION_PIN_VH = 1.15
export const BOARD_STORY_VISION_PIN_VH = 1.75
export const BOARD_VIDEO_PIN_VH = 1
export const BOARD_STATS_PIN_VH = 0.88
export const BOARD_STATS_PIN_TOP_EXTRA = 0
/** Stats map — visible at rest, ramps to full opacity while zooming in */
export const BOARD_STATS_MAP_OPACITY_MIN = 0.28
export const BOARD_STATS_MAP_SCALE_MIN = 0.88
/** Tight scrub keeps motion locked to Lenis scroll — high values feel laggy */
export const BOARD_STATS_SCRUB = true
export const BOARD_DIMENSIONS_PIN_VH = 3.6
/** Scroll scrub lag for dimensions cards — low value keeps motion tied to scroll without feeling delayed */
export const BOARD_DIMENSIONS_SCRUB = 0.12
/** Overlap factor: each card's active window vs stagger step */
export const BOARD_DIMENSIONS_CARD_OVERLAP = 1.7
export const BOARD_PIN_TOP_EXTRA_PX = 48
export const BOARD_MASK_VW_MULTIPLIER = 1.6
export const BOARD_REVEAL_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'
export const BOARD_VELOCITY_SKEW_CLAMP = 12
export const BOARD_STAT_EXIT_Y_PX = -300
export const BOARD_STAT_EXIT_SCALE = 2

export function getScrollHeaderOffset() {
  if (typeof window === 'undefined') return 80
  return window.matchMedia('(min-width: 900px)').matches ? 80 : 72
}

export function getStatsViewportClipHeight() {
  if (typeof window === 'undefined') return 800
  return window.innerHeight - getScrollHeaderOffset() - BOARD_STATS_PIN_TOP_EXTRA - 8
}

/** ScrollTrigger start — pins below fixed site header with breathing room */
export function getBoardPinStart(extraPx = BOARD_PIN_TOP_EXTRA_PX) {
  return `top top+=${getScrollHeaderOffset() + extraPx}`
}

export function getPinScrollDistance(vhMultiplier) {
  return window.innerHeight * vhMultiplier
}

export function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value))
}

export function lerp(start, end, t) {
  return start + (end - start) * t
}

export function mapRange(value, inStart, inEnd, outStart, outEnd) {
  if (inEnd === inStart) return outStart
  const t = clamp((value - inStart) / (inEnd - inStart))
  return lerp(outStart, outEnd, t)
}

export function phaseProgress(progress, start, end) {
  return clamp(mapRange(progress, start, end, 0, 1))
}

export function getDimensionsCanvasOffset(stageWidth, stageHeight, stageScale) {
  const scaledW = DESIGN_WIDTH * stageScale
  const scaledH = DESIGN_HEIGHT * stageScale
  return {
    x: (stageWidth - scaledW) / 2,
    y: (stageHeight - scaledH) / 2,
  }
}

export function computeStudentWelfareImageRect(stageWidth, stageHeight, stageScale, dim) {
  const offset = getDimensionsCanvasOffset(stageWidth, stageHeight, stageScale)
  return {
    left: offset.x + dim.left * stageScale,
    top: offset.y + (dim.top + DIMENSION_CARD_IMAGE_TOP) * stageScale,
    width: DIMENSION_CARD_WIDTH * stageScale,
    height: DIMENSION_CARD_IMAGE_HEIGHT * stageScale,
  }
}

export function measureImageRectRelativeToStage(imageEl, stageEl) {
  if (!imageEl || !stageEl) return null

  const stageRect = stageEl.getBoundingClientRect()
  const imageRect = imageEl.getBoundingClientRect()

  return {
    left: imageRect.left - stageRect.left,
    top: imageRect.top - stageRect.top,
    width: imageRect.width,
    height: imageRect.height,
  }
}

export function computeHeroMorphValues(
  morphPhase,
  stageWidth,
  stageHeight,
  stageScale,
  dim,
  measuredImageRect = null,
) {
  const t = easeOutCubic(morphPhase)
  const end = measuredImageRect ?? computeStudentWelfareImageRect(stageWidth, stageHeight, stageScale, dim)

  const crossfadeEnd = 0.14
  const heroMorphOpacity =
    morphPhase <= 0 ? 0 : morphPhase >= crossfadeEnd ? 1 : easeOutCubic(morphPhase / crossfadeEnd)
  const heroHomeOpacity = 1 - heroMorphOpacity

  const chromeFadeStart = 0.62
  const cardChromeOpacity =
    morphPhase <= 0
      ? 0
      : morphPhase >= 1
        ? 1
        : morphPhase < chromeFadeStart
          ? 0
          : easeOutCubic((morphPhase - chromeFadeStart) / (1 - chromeFadeStart))

  const morphComplete = morphPhase >= 1

  return {
    left: lerp(0, end.left, t),
    top: lerp(0, end.top, t),
    width: lerp(stageWidth, end.width, t),
    height: lerp(stageHeight, end.height, t),
    borderRadius: lerp(24, 20, t),
    useFullBleed: morphPhase <= 0.001,
    heroHomeOpacity,
    heroMorphOpacity,
    cardChromeOpacity,
    flyingOpacity: morphComplete ? 0 : 1,
    cardImageOpacity: morphComplete ? 1 : 0,
    morphActive: morphPhase > 0 && morphPhase < 1,
    morphComplete,
  }
}

export function computeHeroValues(progress) {
  const heroTextPhase = phaseProgress(progress, 0, 0.18)
  const morphPhase = phaseProgress(progress, 0.1, 0.45)

  return {
    heroTextOpacity: 1 - heroTextPhase,
    heroTextY: lerp(0, -48, heroTextPhase),
    morphPhase,
    darkOverlayOpacity: phaseProgress(progress, 0.08, 0.38),
    heroGradientOpacity: 1 - phaseProgress(progress, 0.12, 0.28),
    dimensionsOpacity: phaseProgress(progress, 0.38, 0.48),
    titleOpacity: phaseProgress(progress, 0.42, 0.52),
    cardsPhase: phaseProgress(progress, 0.48, 1),
    cardsInteractive: progress > 0.55,
  }
}

export function computeCardValues(cardsPhase, index, total, dim) {
  const staggerStart = index / total
  const staggerEnd = staggerStart + 0.55
  const cardProgress = phaseProgress(cardsPhase, staggerStart, Math.min(staggerEnd, 1))
  const t = easeOutCubic(cardProgress)

  return {
    opacity: t,
    x: lerp(dim.entryX ?? 0, 0, t),
    y: lerp(dim.entryY ?? 0, 0, t),
    extraRotate: lerp(dim.entryRotate ?? 0, 0, t),
    scale: lerp(0.82, 1, t),
  }
}

export function easeOutCubic(t) {
  return 1 - (1 - t) ** 3
}

export function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
}

export function smoothstep(edge0, edge1, x) {
  const t = clamp((x - edge0) / (edge1 - edge0))
  return t * t * (3 - 2 * t)
}

/** Board-style char opacity reveal (opacity 0.3 → 1, no font-weight ramp) */
export function applyCharOpacityProgress(container, progress) {
  if (!container) return

  const spans = container.querySelectorAll('[data-char-index]')
  const total = spans.length
  if (total === 0) return

  const p = clamp(progress)

  spans.forEach((span, index) => {
    const t = easeOutCubic(clamp(p * total - index))
    span.style.opacity = String(lerp(BOARD_CHAR_OPACITY_MIN, BOARD_CHAR_OPACITY_MAX, t))
  })
}

/** @deprecated Use applyCharOpacityProgress — kept for home scroll if needed */
export function applyBoldTextProgress(container, progress) {
  applyCharOpacityProgress(container, progress)
}
export function computeHighlightZoomValues(progress, initialMaskSize, targetMaskSize) {
  const p = clamp(progress)

  return {
    maskSize: lerp(initialMaskSize, targetMaskSize, p),
    introOpacity: 1 - smoothstep(0, 0.32, p),
    headlineOpacity: 1 - smoothstep(0.04, 0.38, p),
  }
}

export function getHighlightMaskTarget(layoutScale = 1) {
  const scale = layoutScale > 0 ? layoutScale : 1
  return (window.innerWidth * BOARD_MASK_VW_MULTIPLIER) / scale
}

export function computeVisionMissionScrollValues(progress) {
  const p = clamp(progress)
  const visionReveal = smoothstep(0, BOARD_SLIDE_SWAP_THRESHOLD, p)
  const transitionT = smoothstep(
    BOARD_SLIDE_SWAP_THRESHOLD - 0.08,
    BOARD_SLIDE_SWAP_THRESHOLD + 0.08,
    p,
  )
  const missionReveal = smoothstep(BOARD_SLIDE_SWAP_THRESHOLD, 1, p)

  return {
    visionReveal,
    missionReveal,
    visionY: lerp(0, -BOARD_SLIDE_OFFSET_PX, transitionT),
    missionY: lerp(BOARD_SLIDE_OFFSET_PX, 0, transitionT),
    visionOpacity: 1 - transitionT,
    missionOpacity: transitionT,
  }
}

export function getDimensionsPinStart() {
  return 'top top'
}

export function getDimensionsCardTravelY(pinHeight) {
  const h = pinHeight > 0 ? pinHeight : (typeof window !== 'undefined' ? window.innerHeight : 800)
  return h * 0.55
}

export function computeDesktopCardMotion(cardIndex, cardCount, scrollProgress, travelY) {
  const TRAVEL_START_Y = travelY
  const TRAVEL_END_Y = -travelY

  // Staggered windows — next card begins while previous is still travelling upward
  const staggerStep = 1 / (cardCount + 1.5)
  const windowSize = staggerStep * BOARD_DIMENSIONS_CARD_OVERLAP
  const segmentStart = cardIndex * staggerStep
  const segmentEnd = segmentStart + windowSize
  const span = segmentEnd - segmentStart
  const rawT = span > 0 ? (scrollProgress - segmentStart) / span : 0

  if (rawT <= 0 || rawT >= 1) {
    return {
      opacity: 0,
      translateY: rawT <= 0 ? TRAVEL_START_Y : TRAVEL_END_Y,
      pointerEvents: 'none',
      isActive: false,
    }
  }

  const travel = rawT
  const translateY = lerp(TRAVEL_START_Y, TRAVEL_END_Y, travel)
  const opacity = smoothstep(0.1, 0.2, rawT) * (1 - smoothstep(0.8, 0.9, rawT))

  return {
    opacity,
    translateY,
    pointerEvents: opacity > 0.35 && rawT > 0.06 && rawT < 0.94 ? 'auto' : 'none',
    isActive: rawT > 0.15 && rawT < 0.85,
  }
}
